import React, { useState } from 'react';
import DragDropLayout, { triggerHaptic } from './Layouts/DragDropLayout';

const SoundPositionFinderGame = ({ gameData, onComplete }) => {
    const [currentWordIndex, setCurrentWordIndex] = useState(0);
    const [completedWords, setCompletedWords] = useState([]);
    const [score, setScore] = useState(0);
    const [feedback, setFeedback] = useState(null); 
    const [selectedPosition, setSelectedPosition] = useState(null);

    const currentWord = gameData?.words?.[currentWordIndex];
    const totalWords = gameData?.words?.length || 0;
    const pointsPerWord = gameData?.points_per_word || 20;

    const handlePositionSelect = (position) => {
        if (feedback) return;
        triggerHaptic(30);
        setSelectedPosition(position);

        if (position === currentWord.position) {
            setFeedback('correct');
            const newScore = score + pointsPerWord;
            setScore(newScore);
            if (!completedWords.includes(currentWord.id)) {
                setCompletedWords(prev => [...prev, currentWord.id]);
            }
            triggerHaptic([100, 50, 100]);

            setTimeout(() => {
                setFeedback(null);
                setSelectedPosition(null);

                if (currentWordIndex < totalWords - 1) {
                    setCurrentWordIndex(prev => prev + 1);
                } else {
                    triggerHaptic([200, 100, 200, 100, 300]);
                    onComplete?.(newScore);
                }
            }, 3000);
        } else {
            setFeedback('wrong');
            triggerHaptic([50, 50, 50]);
            setTimeout(() => {
                setFeedback(null);
                setSelectedPosition(null);
            }, 2000);
        }
    };

    if (!gameData || !gameData.words || !currentWord) {
        return (
            <div className="premium-glass rounded-xl md:rounded-[3rem] p-4 md:p-12 text-center bg-white animate-pulse">
                <div className="text-6xl md:text-[100px] mb-8">📡</div>
                <h3 className="text-base md:text-3xl font-black text-slate-800 tracking-tighter uppercase">Kraunamas Phonetic Radar...</h3>
            </div>
        );
    }

    const positions = [
        { id: 'start', label: 'PRADŽIA', icon: '⬅️', gradient: 'from-blue-500 to-indigo-600', sub: 'Pirmas garsas' },
        { id: 'middle', label: 'VIDURYS', icon: '↔️', gradient: 'from-amber-500 to-orange-600', sub: 'Kažkur viduje' },
        { id: 'end', label: 'PABAIGA', icon: '➡️', gradient: 'from-rose-500 to-red-600', sub: 'Paskutinis garsas' }
    ];

    const progress = (currentWordIndex / totalWords) * 100;

    const hud = (
        <div className="premium-glass rounded-xl md:rounded-[2.5rem] p-3 md:p-8 border-white/40 shadow-2xl relative overflow-hidden bg-slate-900 text-white">
            <div className="absolute top-0 right-0 w-96 h-96 bg-indigo-500/10 rounded-full blur-[100px] pointer-events-none animate-pulse"></div>
            <div className="flex flex-col md:flex-row items-center justify-between gap-2 md:p-6 relative z-10">
                <div className="flex items-center gap-2 md:gap-4">
                    <div className="w-12 h-12 md:w-20 md:h-20 bg-indigo-500/20 rounded-xl md:rounded-3xl flex items-center justify-center text-xl md:text-4xl shadow-inner border border-white/10 animate-float">
                        📡
                    </div>
                    <div>
                        <h3 className="text-base md:text-3xl font-black tracking-tighter leading-none uppercase bg-clip-text text-transparent bg-gradient-to-r from-indigo-300 to-white">
                            Phonetic Radar
                        </h3>
                    </div>
                </div>

                <div className="flex items-center gap-2 md:gap-4">
                    <div className="bg-white/5 backdrop-blur-md px-4 py-2 md:px-8 md:py-4 rounded-xl md:rounded-[2rem] border border-white/10 shadow-xl">
                        <p className="text-[8px] md:text-[10px] font-black text-indigo-300 uppercase tracking-widest mb-1 opacity-50">Tikslumas</p>
                        <p className="text-sm md:text-2xl font-black text-white">⭐ {score}</p>
                    </div>
                    <div className="bg-white/5 backdrop-blur-md px-4 py-2 md:px-8 md:py-4 rounded-xl md:rounded-[2rem] border border-white/10 shadow-xl text-center min-w-[80px] md:min-w-[120px]">
                        <p className="text-[8px] md:text-[10px] font-black text-indigo-300 uppercase tracking-widest mb-1 opacity-50">Objektas</p>
                        <p className="text-sm md:text-2xl font-black text-white">{currentWordIndex + 1}/{totalWords}</p>
                    </div>
                </div>
            </div>

            <div className="mt-4 md:mt-8 w-full bg-white/5 rounded-full h-2 md:h-4 p-0.5 md:p-1 shadow-inner overflow-hidden border border-white/10">
                <div 
                    className="h-full bg-gradient-to-r from-indigo-500 via-purple-500 to-rose-500 rounded-full transition-all duration-1000"
                    style={{ width: `${progress}%` }}
                />
            </div>
        </div>
    );

    const choices = (
        <div className="space-y-4 md:space-y-8 py-4 md:py-8">
            <div className="premium-glass rounded-2xl md:rounded-[4rem] p-4 md:p-10 shadow-2xl border-white/40 bg-white relative overflow-hidden flex flex-col items-center justify-center min-h-[300px] md:min-h-[400px]">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(99,102,241,0.05)_0%,transparent_70%)] pointer-events-none"></div>
                
                <div className="relative mb-6 md:mb-12">
                    <div className="absolute -inset-10 md:inset-16 bg-indigo-100 rounded-full blur-3xl opacity-30 animate-pulse"></div>
                    <div className="bg-slate-900 px-8 py-4 md:px-14 md:py-10 rounded-xl md:rounded-[3rem] shadow-2xl border-4 border-indigo-100 relative z-10 animate-pop-in">
                        <span className="text-4xl md:text-8xl font-black text-white leading-none drop-shadow-2xl font-mono">{currentWord.target_sound}</span>
                    </div>
                    <div className="absolute -bottom-2 -right-2 md:-bottom-4 md:-right-4 bg-indigo-500 text-white w-10 h-10 md:w-14 md:h-14 rounded-xl md:rounded-2xl flex items-center justify-center text-xl md:text-2xl shadow-xl border-2 md:border-4 border-white animate-bounce">🔍</div>
                </div>

                <div className="text-center space-y-2 md:space-y-4">
                    <div className="text-6xl md:text-[150px] drop-shadow-2xl animate-float py-2 md:py-4">{currentWord.emoji}</div>
                    <h2 className="text-2xl md:text-6xl font-black text-slate-800 tracking-tighter uppercase leading-none font-mono">{currentWord.word}</h2>
                </div>
            </div>
        </div>
    );

    const targets = (
        <div className="space-y-4 md:space-y-8 w-full max-w-4xl mx-auto py-4">
            <div className="premium-glass rounded-2xl md:rounded-[3rem] p-3 md:p-10 shadow-2xl border-white/40 bg-slate-900 relative overflow-hidden flex flex-col items-center justify-center">
                <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-10 pointer-events-none"></div>
                <div className="w-full relative z-10 space-y-2 md:space-y-6">
                    <h4 className="text-[10px] font-black text-indigo-400 uppercase tracking-[0.6em] text-center mb-4 md:mb-10">Pasirinkite garso lokaciją</h4>
                    
                    <div className="flex flex-col gap-2 md:gap-4">
                        {positions.map((pos) => {
                            const isSelected = selectedPosition === pos.id;
                            const isCorrectInFeed = feedback === 'correct' && isSelected;
                            const isWrongInFeed = feedback === 'wrong' && isSelected;

                            return (
                                <button
                                    key={pos.id}
                                    onClick={() => handlePositionSelect(pos.id)}
                                    disabled={!!feedback}
                                    className={`
                                        relative flex items-center gap-3 md:gap-6 p-3 md:p-8 rounded-xl md:rounded-[2.5rem] transition-all duration-500 transform border-b-4 md:border-b-[12px]
                                        ${isCorrectInFeed
                                            ? 'bg-emerald-500 text-white border-emerald-700 scale-105 shadow-2xl'
                                            : isWrongInFeed
                                                ? 'bg-rose-500 text-white border-rose-700 animate-shake scale-95'
                                                : isSelected
                                                    ? 'bg-indigo-600 text-white border-indigo-800 scale-105 shadow-2xl'
                                                    : 'bg-white/5 text-white border-white/10 hover:bg-white/10 active:translate-y-1 active:border-b-0 shadow-xl backdrop-blur-md'}
                                        disabled:cursor-default
                                    `}
                                >
                                    <div className={`w-12 h-12 md:w-24 md:h-24 rounded-lg md:rounded-[2rem] bg-gradient-to-br ${pos.gradient} flex items-center justify-center text-xl md:text-5xl shadow-2xl border-2 md:border-4 border-white/20 shrink-0`}>
                                        {pos.icon}
                                    </div>
                                    <div className="text-left flex-1">
                                        <div className={`text-sm md:text-4xl font-black uppercase tracking-tighter ${isSelected || (feedback && isSelected) ? 'text-white' : 'text-indigo-100'}`}>
                                            {pos.label}
                                        </div>
                                        <div className={`text-[10px] md:text-lg font-black opacity-40 uppercase tracking-widest ${isSelected || (feedback && isSelected) ? 'text-white' : 'text-indigo-300'}`}>
                                            {pos.sub}
                                        </div>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <>
            <DragDropLayout 
                hud={hud}
                choices={choices}
                targets={targets}
            />

            {feedback && (
                <div className="fixed inset-0 flex items-center justify-center z-[2000] p-4">
                    <div className="absolute inset-0 bg-slate-950/80 backdrop-blur-2xl animate-fade-in" />
                    <div className={`
                        max-w-xl w-full p-8 md:p-16 rounded-[3rem] md:rounded-[5rem] bg-white shadow-2xl relative animate-pop-in border-[10px] md:border-[16px]
                        ${feedback === 'correct' ? 'border-emerald-500' : 'border-rose-500'}
                    `}>
                        <div className="absolute -top-12 left-1/2 -translate-x-1/2 w-24 h-24 bg-white rounded-full flex items-center justify-center text-4xl shadow-2xl border-4 border-inherit animate-bounce">
                            {feedback === 'correct' ? '📡' : '🛰️'}
                        </div>
                        
                        <div className="text-center space-y-6 pt-4">
                            <h2 className={`text-2xl md:text-5xl font-black uppercase tracking-tighter ${feedback === 'correct' ? 'text-emerald-600' : 'text-rose-600'}`}>
                                {feedback === 'correct' ? 'Signalas aptiktas!' : 'Signalas prarastas...'}
                            </h2>

                            <div className={`p-6 md:p-10 rounded-2xl md:rounded-[3rem] border-4 text-center ${feedback === 'correct' ? 'bg-emerald-50 border-emerald-100 text-emerald-900' : 'bg-rose-50 border-rose-100 text-rose-900'}`}>
                                <p className="text-sm md:text-2xl font-black leading-tight italic">
                                    {feedback === 'correct' 
                                        ? `Puiku! Garsas "${currentWord.target_sound}" užfiksuotas žodžio ${posLabel(currentWord.position)}.`
                                        : `Klaida... Toje vietoje garso "${currentWord.target_sound}" nėra.`}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

const posLabel = (p) => {
    if (p === 'start') return 'PRADŽIOJE';
    if (p === 'middle') return 'VIDURYJE';
    return 'PABAIGOJE';
};

export default SoundPositionFinderGame;
